import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type {
  CookieConsent,
  CookieConsentCategories,
} from '@/shared/lib/privacy/cookieConsent.types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const COOKIE_CONSENT_STORAGE_KEY = 'winging-cookie-consent';
export const COOKIE_CONSENT_EVENT = 'winging-cookie-consent-change';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'winging-cookie-preferences-open';
const KST_TIMEZONE = 'Asia/Seoul';
const COOKIE_CONSENT_MAX_AGE_DAYS = 180;
const COOKIE_CONSENT_MAX_AGE_SECONDS =
  60 * 60 * 24 * COOKIE_CONSENT_MAX_AGE_DAYS;

const OPTIONAL_TRACKING_COOKIE_PREFIXES = [
  '_ga',
  '_gid',
  '_gat',
  '_clck',
  '_clsk',
];

export const hasBrowserPrivacyOptOut = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorWithPrivacy = window.navigator as Navigator & {
    readonly globalPrivacyControl?: boolean;
    readonly msDoNotTrack?: string | null;
  };
  const windowWithPrivacy = window as Window & {
    readonly doNotTrack?: string | null;
  };

  return (
    navigatorWithPrivacy.globalPrivacyControl === true ||
    navigatorWithPrivacy.doNotTrack === '1' ||
    navigatorWithPrivacy.msDoNotTrack === '1' ||
    windowWithPrivacy.doNotTrack === '1'
  );
};

const clearOptionalTrackingCookies = () => {
  try {
    const cookieNames = document.cookie
      .split(';')
      .map((cookie) => cookie.trim().split('=')[0])
      .filter((name) =>
        OPTIONAL_TRACKING_COOKIE_PREFIXES.some((prefix) =>
          name.startsWith(prefix),
        ),
      );
    const rootDomain = window.location.hostname
      .split('.')
      .slice(-2)
      .join('.');

    cookieNames.forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
      if (rootDomain.includes('.')) {
        document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${rootDomain}; SameSite=Lax`;
      }
    });
  } catch {
    // 선택 쿠키 삭제가 차단돼도 동의 상태 저장은 계속 진행한다.
  }
};

const isCookieConsent = (value: unknown): value is CookieConsent => {
  if (!value || typeof value !== 'object') return false;

  const consent = value as Partial<CookieConsent>;
  return (
    consent.version === 1 &&
    consent.essential === true &&
    typeof consent.analytics === 'boolean' &&
    typeof consent.replay === 'boolean' &&
    typeof consent.updatedAt === 'string' &&
    dayjs(consent.updatedAt).isValid()
  );
};

const isExpiredCookieConsent = (consent: CookieConsent): boolean =>
  dayjs(consent.updatedAt)
    .tz(KST_TIMEZONE)
    .add(COOKIE_CONSENT_MAX_AGE_DAYS, 'day')
    .isBefore(dayjs().tz(KST_TIMEZONE));

const clearStoredCookieConsent = () => {
  try {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
  } catch {
    // localStorage가 차단된 경우 필수 동의 쿠키 삭제를 계속 시도한다.
  }

  try {
    document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch {
    // 쿠키 삭제도 차단된 경우 만료된 동의는 메모리상 무효로 처리한다.
  }
};

export const readCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storedConsent = window.localStorage.getItem(
      COOKIE_CONSENT_STORAGE_KEY,
    );
    if (storedConsent) {
      const parsedConsent: unknown = JSON.parse(storedConsent);
      if (isCookieConsent(parsedConsent)) {
        if (isExpiredCookieConsent(parsedConsent)) {
          clearStoredCookieConsent();
          return null;
        }
        if (hasBrowserPrivacyOptOut()) {
          clearOptionalTrackingCookies();
          return {
            ...parsedConsent,
            analytics: false,
            replay: false,
          };
        }
        return parsedConsent;
      }
    }
  } catch {
    // localStorage 접근 실패 시 필수 동의 쿠키를 확인한다.
  }

  try {
    const storedCookie = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) =>
        cookie.startsWith(`${COOKIE_CONSENT_STORAGE_KEY}=`),
      );
    if (!storedCookie) return null;

    const parsedConsent: unknown = JSON.parse(
      decodeURIComponent(storedCookie.split('=').slice(1).join('=')),
    );
    if (!isCookieConsent(parsedConsent)) return null;
    if (isExpiredCookieConsent(parsedConsent)) {
      clearStoredCookieConsent();
      return null;
    }
    if (hasBrowserPrivacyOptOut()) {
      clearOptionalTrackingCookies();
      return {
        ...parsedConsent,
        analytics: false,
        replay: false,
      };
    }
    return parsedConsent;
  } catch {
    // 필수 동의 쿠키도 읽을 수 없으면 동의하지 않은 상태로 처리한다.
    return null;
  }
};

export const saveCookieConsent = (
  categories: CookieConsentCategories,
): CookieConsent => {
  const privacyOptOut = hasBrowserPrivacyOptOut();
  const consent: CookieConsent = {
    ...categories,
    essential: true,
    analytics: privacyOptOut ? false : categories.analytics,
    replay: privacyOptOut ? false : categories.replay,
    version: 1,
    updatedAt: dayjs().tz(KST_TIMEZONE).toISOString(),
  };

  if (!consent.analytics) {
    clearOptionalTrackingCookies();
  }

  try {
    window.localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify(consent),
    );
  } catch {
    // localStorage가 차단된 경우 필수 동의 쿠키를 사용한다.
  }

  try {
    const secureAttribute =
      window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=${encodeURIComponent(
      JSON.stringify(consent),
    )}; Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secureAttribute}`;
  } catch {
    // 쿠키도 차단된 경우 현재 페이지에만 동의 상태를 반영한다.
  }

  window.dispatchEvent(
    new CustomEvent<CookieConsent>(COOKIE_CONSENT_EVENT, {
      detail: consent,
    }),
  );

  return consent;
};
